<template>
  <div class="tw-w-full tw-flex tw-flex-col tw-gap-3">
    <div class="tw-flex tw-items-end tw-gap-2 tw-text-xl tw-font-bold">
      <span class="tw-text-[#F1583D]">3.</span> 
      <span>Proposed Portfolio</span>
      <span class="tw-text-sm tw-italic tw-font-normal">(optional)</span>
    </div>

    <div class="tw-flex tw-items-center tw-gap-2">
      <PortfolioSearchSelect
        placeholder="Search for proposed portfolio"
        @selected="handleSelected"
      />
      <LoadBestFitButton 
        v-if="advisorStore.advisor?.firm?.has_modelsets || checkupStore.featureFlags.includes('Feature: Model Matchmaker')"
        :selected-model-sets="selectedModelSets"
        :selected-strategies="selectedStrategies"
      />
      <OptimizeAssetLocationButton v-if="checkupStore.optimizedPortfolio" />
      
      <!-- Advanced Toggle Button -->
      <button
        v-if="advisorStore.advisor?.firm?.has_modelsets || checkupStore.featureFlags.includes('Feature: Model Matchmaker')"
        @click="toggleAdvancedSection"
        class="tw-text-[#F1583D] tw-underline tw-cursor-pointer tw-bg-transparent tw-border-none tw-flex tw-items-center tw-font-medium tw-ml-2"
      >
        Advanced
        <i 
          class="fas fa-chevron-down tw-ml-1 tw-transition-transform tw-duration-200"
          :class="{ 'tw-rotate-180': showAdvancedSection }"
        />
      </button>
    </div>

    <!-- Advanced Configuration Section -->
    <div 
      v-if="showAdvancedSection"
      class="tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 tw-space-y-4"
    >
      <h4 class="tw-font-medium tw-text-gray-800 tw-mb-3">Advanced Configuration</h4>
      
      <!-- Model Sets Section -->
      <div class="tw-space-y-3">
        <h5 class="tw-font-medium tw-text-gray-700 tw-text-sm">Model Sets</h5>
        
        <div v-if="loadingModelSets" class="tw-flex tw-items-center tw-py-2">
          <i class="fa fa-spinner fa-spin tw-text-sm tw-mr-2" />
          <span class="tw-text-sm tw-text-gray-600">Loading model sets...</span>
        </div>
        
        <div v-else-if="modelSets.length > 0" class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-2">
          <label 
            v-for="modelSet in modelSets" 
            :key="modelSet.id"
            class="tw-flex tw-items-center tw-cursor-pointer tw-hover:bg-white tw-p-2 tw-rounded tw-border tw-border-transparent hover:tw-border-gray-200"
          >
            <input
              type="checkbox"
              v-model="selectedModelSets"
              :value="modelSet.id"
              class="tw-mr-2 tw-cursor-pointer"
            />
            <span class="tw-text-sm">{{ modelSet.name }}</span>
            <span v-if="modelSet.model_portfolios && modelSet.model_portfolios.length" class="tw-text-xs tw-text-gray-500 tw-ml-1">
              ({{ modelSet.model_portfolios.length }})
            </span>
          </label>
        </div>

        <div v-else-if="!loadingModelSets" class="tw-text-gray-500 tw-text-sm tw-py-2">
          No model sets available
        </div>

        <div v-if="modelSetsError" class="tw-text-red-500 tw-text-sm tw-py-2">
          Error loading model sets: {{ modelSetsError }}
        </div>
      </div>

      <!-- Tags Section -->
      <div class="tw-space-y-3">
        <h5 class="tw-font-medium tw-text-gray-700 tw-text-sm">Tags</h5>

        <div v-if="loadingTags" class="tw-flex tw-items-center tw-py-2">
          <i class="fa fa-spinner fa-spin tw-text-sm tw-mr-2" />
          <span class="tw-text-sm tw-text-gray-600">Loading tags...</span>
        </div>

        <div v-else-if="tags.length > 0" class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-2">
          <label
            class="tw-flex tw-items-center tw-cursor-pointer tw-hover:bg-white tw-p-2 tw-rounded tw-border tw-border-transparent hover:tw-border-gray-200"
          >
            <input
              type="checkbox"
              v-model="selectedStrategies"
              :value="'no_tag'"
              class="tw-mr-2 tw-cursor-pointer"
            />
            <span class="tw-text-sm">No strategy tag</span>
          </label>
          <label 
            v-for="tag in tags" 
            :key="tag.id"
            class="tw-flex tw-items-center tw-cursor-pointer tw-hover:bg-white tw-p-2 tw-rounded tw-border tw-border-transparent hover:tw-border-gray-200"
          >
            <input
              v-model="selectedStrategies"
              type="checkbox"
              :value="tag.id"
              class="tw-mr-2 tw-cursor-pointer"
            />
            <span class="tw-text-sm">{{ tag.name }}</span>
            <span v-if="tag.model_portfolios && tag.model_portfolios.length" class="tw-text-xs tw-text-gray-500 tw-ml-1">
              ({{ tag.model_portfolios.length }})
            </span>
          </label>
        </div>
        
        <div v-else-if="!loadingTags" class="tw-text-gray-500 tw-text-sm tw-py-2">
          No tags available
        </div>
        
        <div v-if="tagsError" class="tw-text-red-500 tw-text-sm tw-py-2">
          Error loading tags: {{ tagsError }}
        </div>
      </div>
    </div>

    <!-- Selected Portfolios - moved inside the main wrapper -->
    <SelectedPortfolios
      portfolio-type="optimizedPortfolio"
      placeholder="No proposed portfolio selected"
      :portfolios="checkupStore.optimizedPortfolio ? [checkupStore.optimizedPortfolio] : []"
      @remove="handleRemove"
    />
  </div>
</template>

<script>
import PortfolioSearchSelect from './PortfolioSearchSelect.vue';
import SelectedPortfolios from './SelectedPortfolios.vue';
import LoadBestFitButton from './LoadBestFitButton.vue';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';
import { mapStores } from 'pinia';
import OptimizeAssetLocationButton from './OptimizeAssetLocationButton.vue';

export default {
  name: 'OptimizedProposedSelectWrapper',
  components: { PortfolioSearchSelect, SelectedPortfolios, LoadBestFitButton, OptimizeAssetLocationButton},
  data() {
    return {
      showAdvancedSection: false,
      loadingModelSets: false,
      modelSets: [],
      selectedModelSets: [],
      modelSetsError: null,
      loadingTags: false,
      tags: [],
      selectedStrategies: [],
      tagsError: null
    };
  },
  computed: { 
    ...mapStores(useCheckupStore, useAdvisorStore),
  },
  methods: {
    handleSelected(portfolio) {
      this.checkupStore.setOptimizedPortfolio(portfolio);
    },
    handleRemove(portfolio) {
      this.checkupStore.removePortfolio(portfolio);
    },
    toggleAdvancedSection() {
      this.showAdvancedSection = !this.showAdvancedSection;
      
      if (this.showAdvancedSection) {
        if (this.modelSets.length === 0 && !this.loadingModelSets) {
          this.fetchModelSets();
        }
        if (this.tags.length === 0 && !this.loadingTags) {
          this.fetchTags();
        }
      }
    },
    async fetchModelSets() {
      this.loadingModelSets = true;
      this.modelSetsError = null;
      
      try {
        const response = await fetch('/api/v1/model_sets/');
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('Fetched model sets:', data);
        
        this.modelSets = (data.model_sets || []).map(modelSet => {
          console.log(`Model Set ${modelSet.id} portfolios:`, modelSet.model_portfolios);
          return {
            ...modelSet,
            model_portfolios: modelSet.model_portfolios || []  // Ensure portfolios are included
          };
        });
        
        // Select all model sets by default
        this.selectedModelSets = this.modelSets.map(modelSet => modelSet.id);
        
        console.log('Model Sets after initialization:', this.modelSets);
        console.log('All model sets selected by default:', this.selectedModelSets);
      } catch (error) {
        console.error('Failed to fetch model sets:', error);
        this.modelSetsError = error.message;
      } finally {
        this.loadingModelSets = false;
      }
    },
    async fetchTags() {
      this.loadingTags = true;
      this.tagsError = null;
      
      try {
        const response = await fetch('/api/v1/strategies/');
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('Fetched tags:', data);
        
        this.tags = (data.strategies || []).map(tag => {
          console.log(`Tag ${tag.id} portfolios:`, tag.model_portfolios);
          return {
            ...tag,
            model_portfolios: tag.model_portfolios || []
          };
        });
        
        // Select all tags by default
        this.selectedStrategies = ['no_tag', ...this.tags.map(tag => tag.id)];
        
        console.log('Tags after initialization:', this.tags);
        console.log('All tags selected by default:', this.selectedStrategies);
      } catch (error) {
        console.error('Failed to fetch tags:', error);
        this.tagsError = error.message;
      } finally {
        this.loadingTags = false;
      }
    }
  },
};
</script>